/**
 * 台本契約の版と「声」の話者の識別子 (docs/features/brand-cut-motion-graphics-plan.md §10.7.2 / §10.7.3)。
 *
 * - `scenarios.generation_metadata.script_contract = 'motion-v2'` のシナリオが声の台本 (画面設計・声・画面の文字)。
 *   UI の分岐は production_engine ではなくこの値で行う (2e / 2f で作った旧契約のモーション台本は従来の UI のまま・本文を消さない)
 * - 声の話者の識別子 speaker_id は `narration` / `character:<key>` / `actor:<actor_kind>:<actor_id>` / `label:<label>`
 *   (backend の voice_map のキーと同じ)。表示名ではないので改名しても声が追随する
 *
 * 正本は backend (motion_script.py / mention_tokens.py)。ここは画面の出し分けと識別子の組み立てだけを持つ。
 */
import type { DialogueLine, DialogueLineSpeaker } from './types';

export const SCRIPT_CONTRACT_MOTION_V2 = 'motion-v2';

/** 声の台本の行数 / 1 行の字数の上限 (backend MOTION_V2_DIALOGUE_LINES_MAX_COUNT / _TEXT_MAX_CHARS と同値) */
export const MOTION_V2_DIALOGUE_LINES_MAX_COUNT = 8;
export const MOTION_V2_DIALOGUE_LINE_TEXT_MAX_CHARS = 60;

/** ナレーター話者の表示名 (backend NARRATION_SPEAKER_LABEL と同じ) */
export const NARRATION_VOICE_SPEAKER_LABEL = 'ナレーター';
/** ナレーターの speaker_id (予約語) */
export const NARRATION_SPEAKER_ID = 'narration';

type ScenarioMetaSource = { generation_metadata?: Record<string, unknown> | null } | null | undefined;

function metaOf(scenario: ScenarioMetaSource): Record<string, unknown> {
  const meta = scenario?.generation_metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

/** 声の台本 (script_contract='motion-v2') のシナリオか。 */
export function isMotionV2Script(scenario: ScenarioMetaSource): boolean {
  return metaOf(scenario).script_contract === SCRIPT_CONTRACT_MOTION_V2;
}

/** 旧契約のモーション台本 (production_engine='motion' で script_contract の無いシナリオ = 変換の対象) か。 */
export function isLegacyMotionScript(scenario: ScenarioMetaSource): boolean {
  const meta = metaOf(scenario);
  return meta.production_engine === 'motion' && !meta.script_contract;
}

/** ナレーター話者 (声の台本の予約語 narration の解決結果)。 */
export function narrationVoiceSpeaker(): DialogueLineSpeaker {
  return { kind: 'narration', label: NARRATION_VOICE_SPEAKER_LABEL, actor_id: null, actor_kind: null };
}

/** 話者がナレーターか (kind='narration')。 */
export function isNarrationVoiceSpeaker(speaker: Pick<DialogueLineSpeaker, 'kind'> | null | undefined): boolean {
  return speaker?.kind === 'narration';
}

/** 声の行の話者 → speaker_id (backend の voice_map のキー)。 */
export function speakerIdOf(speaker: DialogueLineSpeaker): string {
  if (speaker.kind === 'narration') return NARRATION_SPEAKER_ID;
  if (speaker.actor_id && speaker.actor_kind) return `actor:${speaker.actor_kind}:${speaker.actor_id}`;
  if (speaker.character_key) return `character:${speaker.character_key}`;
  return `label:${(speaker.label ?? '').trim()}`;
}

/** 登場人物の speaker_id。 */
export function characterSpeakerId(key: string): string {
  return `character:${key}`;
}

/** 声の行の line_id を払い出す (8 桁の 16 進・既存と重複しない)。BE の形式 (^[0-9a-f]{8}$) と同じ。 */
export function newVoiceLineId(existing: Iterable<string> = []): string {
  const taken = new Set(existing);
  for (;;) {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    if (!taken.has(id)) return id;
  }
}

/** line_id の無い行に新しい id を付けた配列を返す (既存行の id は変えない)。 */
export function ensureVoiceLineIds<L extends Pick<DialogueLine, 'line_id'>>(lines: L[]): L[] {
  const taken = lines.map((l) => l.line_id).filter((id): id is string => !!id);
  const out: L[] = [];
  for (const line of lines) {
    if (line.line_id) {
      out.push(line);
      continue;
    }
    const id = newVoiceLineId(taken);
    taken.push(id);
    out.push({ ...line, line_id: id });
  }
  return out;
}
